/**
 * src/utils/sse/parser.js —— SSE 帧解析与事件规范化（纯函数，无副作用）
 *
 * 事件契约对齐后端 LingXiEvent（见 LingXi-Backend DifyEventAdapter / AiTaskExecutionServiceImpl）：
 *  SSE 帧 = { id: eventId, event: eventType, data: LingXiEvent JSON }，
 *  data JSON = { eventId, sequence, eventType, taskId, conversationId, occurredAt, status, payloadVersion, payload }。
 */

/**
 * 把一段 SSE 文本解析为帧对象数组 [{ id, event, data }]。
 * 支持 \n 与 \r\n 行尾、多行 data（按 \n 拼接）、event:/id: 字段、: 注释行。
 * data 为 '[DONE]' 时原样保留，由调用方判定结束。
 * @param {string} text 完整帧文本（帧间以空行分隔；不完整帧由连接器缓冲处理）
 * @returns {Array<{id?: string, event: string, data: string}>}
 */
export function parseSseChunk(text) {
  if (typeof text !== 'string' || text.length === 0) {
    return [];
  }
  return text
    .replace(/\r\n/g, '\n')
    .split(/\n\n+/)
    .map((frame) => frame.trim())
    .filter((frame) => frame.length > 0)
    .map(parseSseFrame)
    .filter(Boolean);
}

function parseSseFrame(frameText) {
  let id;
  let event = 'message';
  const dataLines = [];
  for (const line of frameText.split('\n')) {
    if (line.startsWith(':')) {
      continue; // 注释行（心跳等）
    }
    if (line.startsWith('data:')) {
      dataLines.push(line.slice(5).replace(/^ /, ''));
    } else if (line.startsWith('event:')) {
      event = line.slice(6).replace(/^ /, '') || 'message';
    } else if (line.startsWith('id:')) {
      id = line.slice(3).replace(/^ /, '');
    }
    // retry: 等其他字段忽略
  }
  if (dataLines.length === 0) {
    return null;
  }
  return { id: id || undefined, event, data: dataLines.join('\n') };
}

/**
 * 把后端 LingXiEvent JSON 规范化为前端事件对象。
 * @param {object} json 后端事件 JSON（含 eventId/eventType/sequence/...）
 * @returns {{type, seq, eventId, taskId, conversationId, time, status, payload, raw}|null}
 *          解析失败（缺 eventId/eventType 或非对象）返回 null
 */
export function parseEvent(json) {
  if (!json || typeof json !== 'object') {
    return null;
  }
  const { eventId, eventType, sequence } = json;
  if (typeof eventId !== 'string' || eventId.length === 0) {
    return null;
  }
  if (typeof eventType !== 'string' || eventType.length === 0) {
    return null;
  }
  return {
    type: eventType,
    seq: Number.isFinite(sequence) ? sequence : undefined,
    eventId,
    taskId: json.taskId || undefined,
    conversationId: json.conversationId || undefined,
    time: json.occurredAt || undefined,
    status: json.status || undefined,
    payload: json.payload && typeof json.payload === 'object' ? json.payload : {},
    raw: json,
  };
}